/* import __COLOCATED_TEMPLATE__ from './refund-reasons-config.hbs'; */
/* RESPONSIBLE TEAM: team-integration-apps */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { action } from '@ember/object';
import { type TaskGenerator } from 'ember-concurrency';
import type IntlService from 'embercom/services/intl';
import type SessionService from 'embercom/services/session';
import ajax from 'embercom/lib/ajax';
import { captureException } from 'embercom/lib/sentry';

const MAX_REFUND_REASONS = 10;
const MAX_REASON_LENGTH = 100;

interface RefundReasonsConfigSignature {
  Args: {};
}

export default class RefundReasonsConfig extends Component<RefundReasonsConfigSignature> {
  @service declare intl: IntlService;
  @service declare notificationsService: any;
  @service declare session: SessionService;

  @tracked refundReasons: string[] = [];
  @tracked isEditing = false;
  @tracked isSaving = false;
  @tracked errors: string[] = [];

  constructor(owner: unknown, args: RefundReasonsConfigSignature['Args']) {
    super(owner, args);
    void taskFor(this.loadRefundReasons).perform();
  }

  get canAddMore() {
    return this.refundReasons.length < MAX_REFUND_REASONS;
  }

  get hasUnsavedChanges() {
    return this.isEditing;
  }

  @restartableTask
  *loadRefundReasons(): TaskGenerator<void> {
    try {
      let response: any = yield ajax({
        type: 'GET',
        url: `/ember/shopify/integrations/workspace_refund_reasons?app_id=${this.session.workspace.id}`,
      });

      if (response && response.reasons && Array.isArray(response.reasons)) {
        this.refundReasons = [...response.reasons];
      }
    } catch (error: unknown) {
      captureException(error);
      // Default reasons if fetch fails - these will be replaced by workspace-configured reasons
      this.refundReasons = [];
    }
  }

  @dropTask
  *saveRefundReasons(): TaskGenerator<void> {
    this.errors = [];

    // Validate before saving
    let validationErrors = this.validateReasons();
    if (validationErrors.length > 0) {
      this.errors = validationErrors;
      return;
    }

    this.isSaving = true;

    try {
      yield ajax({
        type: 'PUT',
        url: `/ember/shopify/integrations/update_workspace_refund_reasons?app_id=${this.session.workspace.id}`,
        data: JSON.stringify({
          refund_reason_options: this.refundReasons.filter((r) => r.trim().length > 0),
        }),
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('appstore.settings.shopify.refund-reasons.save-success'),
      );
      this.isEditing = false;
    } catch (error: unknown) {
      if (error && typeof error === 'object' && 'responseJSON' in error) {
        let responseError = error as { responseJSON?: { error?: { message?: string } } };
        if (responseError.responseJSON?.error?.message) {
          this.errors = [responseError.responseJSON.error.message];
        } else {
          this.notificationsService.notifyResponseError(error, {
            default: this.intl.t('appstore.settings.shopify.refund-reasons.save-failure'),
          });
        }
      } else {
        this.notificationsService.notifyResponseError(error, {
          default: this.intl.t('appstore.settings.shopify.refund-reasons.save-failure'),
        });
      }
    } finally {
      this.isSaving = false;
    }
  }

  @action
  updateReason(index: number, event: Event) {
    let target = event.target as HTMLInputElement;
    let newReasons = [...this.refundReasons];
    newReasons[index] = target.value;
    this.refundReasons = newReasons;
    this.isEditing = true;
    this.errors = [];
  }

  @action
  addReason() {
    if (this.canAddMore) {
      this.refundReasons = [...this.refundReasons, ''];
      this.isEditing = true;
    }
  }

  @action
  removeReason(index: number) {
    this.refundReasons = this.refundReasons.filter((_, i) => i !== index);
    this.isEditing = true;
    this.errors = [];
  }

  @action
  reorderReasons(reordered: string[]) {
    this.refundReasons = reordered;
    this.isEditing = true;
    this.errors = [];
  }

  @action
  cancelChanges() {
    void taskFor(this.loadRefundReasons).perform();
    this.isEditing = false;
    this.errors = [];
  }

  @action
  saveChanges() {
    void taskFor(this.saveRefundReasons).perform();
  }

  private validateReasons(): string[] {
    let errors: string[] = [];
    let nonEmptyReasons = this.refundReasons.filter((r) => r.trim().length > 0);

    if (nonEmptyReasons.length === 0) {
      errors.push(this.intl.t('appstore.settings.shopify.refund-reasons.validation.at-least-one'));
      return errors;
    }

    if (nonEmptyReasons.length > MAX_REFUND_REASONS) {
      errors.push(
        this.intl.t('appstore.settings.shopify.refund-reasons.validation.max-reasons', {
          max: MAX_REFUND_REASONS,
        }),
      );
    }

    nonEmptyReasons.forEach((reason, index) => {
      if (reason.length > MAX_REASON_LENGTH) {
        errors.push(
          this.intl.t('appstore.settings.shopify.refund-reasons.validation.too-long', {
            index: index + 1,
            max: MAX_REASON_LENGTH,
          }),
        );
      }
    });

    // Check for duplicates
    let uniqueReasons = new Set(nonEmptyReasons.map((r) => r.trim().toLowerCase()));
    if (uniqueReasons.size !== nonEmptyReasons.length) {
      errors.push(this.intl.t('appstore.settings.shopify.refund-reasons.validation.duplicates'));
    }

    return errors;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Appstore::AppPackage::Settings::Shopify::RefundReasonsConfig': typeof RefundReasonsConfig;
    'appstore/app-package/settings/shopify/refund-reasons-config': typeof RefundReasonsConfig;
  }
}
